<template>
  <div class=" bg-gray-50">
    <HeaderDefault />

    <UPageHero
      title="Join Our Community"
      description="Connect with peers, access exclusive content, and stay updated with the latest academic events."
    />

    <!-- Form Section -->
    <section class="bg-white max-w-screen-xl mx-auto p-12 my-20" >
      <div class="max-w-4xl mx-auto">
        <form @submit="requestMembership" class="space-y-8">
          <div
            v-for="(section, sIndex) in form.form"
            :key="sIndex"
            class="space-y-6"
          >
            <h2
              v-if="section.section"
              class="text-2xl font-semibold text-gray-900"
            >
              {{ section.section }}
            </h2>

            <div class="space-y-4">
              <!-- Name Fields Row -->
              <div class=" gap-4 grid-cols-1 grid sm:grid-cols-3">
                <div >
                  <label class="block text-sm font-medium text-gray-700">
                    Title <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="formv.title"
                    required
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white"
                  >
                    <option value="" disabled selected>Select title</option>
                    <option
                      v-for="title in academicTitles"
                      :key="title.value"
                      :value="title.value"
                    >
                      {{ title.label }}
                    </option>
                  </select>
                </div>

                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700">
                    First Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    v-model="formv.first_name"
                    placeholder="Enter your first name"
                    required
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  />
                </div>

                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700">
                    Last Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    v-model="formv.last_name"
                    placeholder="Enter your last name"
                    required
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  />
                </div>
              </div>

              <!-- Other Fields -->
              <div
                v-for="(field, fIndex) in section.fields.slice(3)"
                :key="fIndex"
              >
                <div v-if="field.type === 'select'" class="space-y-2">
                  <label
                    :for="field.name"
                    class="block text-sm font-medium text-gray-700"
                  >
                    {{ field.label }}
                    <span v-if="field.required" class="text-red-500">*</span>
                  </label>
                  <select
                    :id="field.name"
                    v-model="formv[field.name]"
                    :required="field.required"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white"
                  >
                    <option value="" disabled selected>
                      {{ field.placeholder }}
                    </option>
                    <option
                      v-for="option in field.options"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <!-- Text, Email, URL Inputs -->
                <div
                  v-if="['text', 'email', 'url'].includes(field.type)"
                  class="space-y-2"
                >
                  <label
                    :for="field.name"
                    class="block text-sm font-medium text-gray-700"
                  >
                    {{ field.label }}
                    <span v-if="field.required" class="text-red-500">*</span>
                  </label>
                  <input
                    :id="field.name"
                    :type="field.type === 'tel' ? 'tel' : field.type"
                    v-model="formv[field.name]"
                    :placeholder="field.placeholder"
                    :required="field.required"
                    :pattern="
                      field.validation === 'tel' ? '[0-9]{10}' : undefined
                    "
                    :title="
                      field.validation === 'tel'
                        ? 'Please enter exactly 10 digits'
                        : undefined
                    "
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  />
                  <p
                    v-if="field.validation === 'tel'"
                    class="text-sm text-gray-500 mt-1"
                  >
                    Please enter exactly 10 digits without spaces or special
                    characters
                  </p>
                </div>

                <div v-if="field.type === 'tel'" class="space-y-2">
                  <label
                    :for="field.name"
                    class="block text-sm font-medium text-gray-700"
                  >
                    {{ field.label }}
                    <span v-if="field.required" class="text-red-500">*</span>
                  </label>
                  <div class="flex gap-2">
                    <select
                      v-model="formv.country_code"
                      required
                      class="w-1/3 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white"
                    >
                      <option value="" disabled selected>Select Code</option>
                      <option
                        v-for="code in countryCodes"
                        :key="code.value"
                        :value="code.value"
                      >
                        {{ code.label }}
                      </option>
                    </select>
                    <input
                      :id="field.name"
                      type="tel"
                      v-model="formv[field.name]"
                      placeholder="1234567890"
                      required
                      pattern="[0-9]{10}"
                      title="Please enter exactly 10 digits"
                      class="w-2/3 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    />
                  </div>
                  <p class="text-sm text-gray-500 mt-1">
                    Please select country code and enter 10 digits without
                    spaces or special characters
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-4 mb-6">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="terms"
                  type="checkbox"
                  v-model="formv.agreement"
                  required
                  class="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/20"
                />
              </div>
              <div class="ml-3">
                <label for="terms" class="text-sm text-gray-600">
                  I agree to the
                  <nuxt-link
                    to="/about/terms"
                    class="text-primary hover:text-primary/80 underline"
                  >
                    Terms and Conditions
                  </nuxt-link>
                  and
                  <nuxt-link
                    to="/about/terms"
                    class="text-primary hover:text-primary/80 underline"
                  >
                    Privacy Policy
                  </nuxt-link>
                  <span class="text-red-500">*</span>
                </label>
              </div>
            </div>
          </div>
          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isSubmitting || !formv.agreement"
            class="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            <span>{{ isSubmitting ? "Processing..." : "Submit" }}</span>
            <span v-if="isSubmitting" class="animate-spin">↻</span>
          </button>
        </form>
      </div>
    </section>

    <UMembers :showBrowseButton="false"></UMembers>
    
  </div>   
  <FooterDefault></FooterDefault>>
</template>

<script setup>
const formv = ref({
  title: "",
  first_name: "",
  last_name: "",
  email: "",
  country_code: "",
  contact_number: "",
  institution: "",
  agreement: false,
  preferred_event_types: [],
});

const countryCodes = [
  { value: "+93", label: "🇦🇫 +93 (Afghanistan)" },
  { value: "+355", label: "🇦🇱 +355 (Albania)" },
  { value: "+213", label: "🇩🇿 +213 (Algeria)" },
  { value: "+61", label: "🇦🇺 +61 (Australia)" },
  { value: "+43", label: "🇦🇹 +43 (Austria)" },
  { value: "+880", label: "🇧🇩 +880 (Bangladesh)" },
  { value: "+32", label: "🇧🇪 +32 (Belgium)" },
  { value: "+55", label: "🇧🇷 +55 (Brazil)" },
  { value: "+1", label: "🇨🇦 +1 (Canada)" },
  { value: "+86", label: "🇨🇳 +86 (China)" },
  { value: "+45", label: "🇩🇰 +45 (Denmark)" },
  { value: "+20", label: "🇪🇬 +20 (Egypt)" },
  { value: "+33", label: "🇫🇷 +33 (France)" },
  { value: "+49", label: "🇩🇪 +49 (Germany)" },
  { value: "+30", label: "🇬🇷 +30 (Greece)" },
  { value: "+852", label: "🇭🇰 +852 (Hong Kong)" },
  { value: "+91", label: "🇮🇳 +91 (India)" },
  { value: "+62", label: "🇮🇩 +62 (Indonesia)" },
  { value: "+98", label: "🇮🇷 +98 (Iran)" },
  { value: "+353", label: "🇮🇪 +353 (Ireland)" },
  { value: "+972", label: "🇮🇱 +972 (Israel)" },
  { value: "+39", label: "🇮🇹 +39 (Italy)" },
  { value: "+81", label: "🇯🇵 +81 (Japan)" },
  { value: "+965", label: "🇰🇼 +965 (Kuwait)" },
  { value: "+60", label: "🇲🇾 +60 (Malaysia)" },
  { value: "+52", label: "🇲🇽 +52 (Mexico)" },
  { value: "+977", label: "🇳🇵 +977 (Nepal)" },
  { value: "+31", label: "🇳🇱 +31 (Netherlands)" },
  { value: "+64", label: "🇳🇿 +64 (New Zealand)" },
  { value: "+47", label: "🇳🇴 +47 (Norway)" },
  { value: "+92", label: "🇵🇰 +92 (Pakistan)" },
  { value: "+63", label: "🇵🇭 +63 (Philippines)" },
  { value: "+351", label: "🇵🇹 +351 (Portugal)" },
  { value: "+974", label: "🇶🇦 +974 (Qatar)" },
  { value: "+7", label: "🇷🇺 +7 (Russia)" },
  { value: "+966", label: "🇸🇦 +966 (Saudi Arabia)" },
  { value: "+65", label: "🇸🇬 +65 (Singapore)" },
  { value: "+27", label: "🇿🇦 +27 (South Africa)" },
  { value: "+82", label: "🇰🇷 +82 (South Korea)" },
  { value: "+34", label: "🇪🇸 +34 (Spain)" },
  { value: "+94", label: "🇱🇰 +94 (Sri Lanka)" },
  { value: "+46", label: "🇸🇪 +46 (Sweden)" },
  { value: "+41", label: "🇨🇭 +41 (Switzerland)" },
  { value: "+886", label: "🇹🇼 +886 (Taiwan)" },
  { value: "+66", label: "🇹🇭 +66 (Thailand)" },
  { value: "+90", label: "🇹🇷 +90 (Turkey)" },
  { value: "+971", label: "🇦🇪 +971 (UAE)" },
  { value: "+44", label: "🇬🇧 +44 (UK)" },
  { value: "+1", label: "🇺🇸 +1 (USA)" },
  { value: "+84", label: "🇻🇳 +84 (Vietnam)" }
];
// Add titles list
const academicTitles = [
  { value: "Dr.", label: "Dr." },
  { value: "Prof.", label: "Prof." },
  { value: "Mr.", label: "Mr." },
  { value: "Ms.", label: "Ms." },
  { value: "Mrs.", label: "Mrs." },
  { value: "Mx.", label: "Mx." },
];

// Update form configuration
const form = {
  form: [
    {
      section: "Membership Form",
      fields: [
        {
          label: "Title",
          type: "select",
          name: "title",
          placeholder: "Select your title",
          options: academicTitles,
          required: true,
        },
        {
          label: "First Name",
          type: "text",
          name: "first_name",
          placeholder: "Enter your first name",
          validation: "text",
          required: true,
        },
        {
          label: "Last Name",
          type: "text",
          name: "last_name",
          placeholder: "Enter your last name",
          validation: "text",
          required: true,
        },
        {
          label: "Email Address",
          type: "email",
          name: "email",
          placeholder: "your.email@institution.edu",
          validation: "email",
          required: true,
        },
        {
          label: "Contact Number",
          type: "tel",
          name: "contact_number",
          placeholder: "1234567890",
          validation: "tel",
          required: true,
          hasCountryCode: true,
        },
        {
          label: "Institution/Organization",
          type: "text",
          name: "institution",
          placeholder: "Your university or organization",
          validation: "text",
          required: true,
        },
        {
          type: "submit",
          name: "Submit Application",
        },
      ],
    },
  ],
};

const isSubmitting = ref(false);

async function requestMembership(event) {
  event.preventDefault();
  isSubmitting.value = true;

  formv.value.contact_number = `${formv.value.country_code}${formv.value.contact_number}`;
  delete formv.value.country_code;
  try {
    const { data: resp } = await useFetch(
      "https://backend.postfeed.in/api/aa-users",
      {
        method: "post",
        body: { data: { ...formv.value, publishedAt: null } },
      }
    );

    if (resp.value?.data) {
      alert(
        "✅ Application submitted successfully! We will review your application and get back to you soon."
      );
      formv.value = {
        title: "",
        first_name: "",
        last_name: "",
        preferred_event_types: [],
        agreement: [],
      };
      await navigateTo("/thankyou");
    } else {
      throw new Error("Submission failed");
    }
  } catch (error) {
    alert(
      "❌ Something went wrong. Please try again or contact support at academicalertshq@gmail.com"
    );
  } finally {
    isSubmitting.value = false;
  }
}

// Page metadata
useHead({
  title: "Become a Member - Academic Alerts",
  meta: [
    {
      name: "description",
      content:
        "Join our academic community to access exclusive content and connect with peers.",
    },
  ],
});
</script>
